import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import {
  adminListReservations,
  adminUpdateReservationStatus,
  adminDeleteReservation,
  adminListTables,
  getMyRole,
} from "@/lib/reservations.functions";
import { Button } from "@/components/ui/button";
import { NewReservationDialog } from "@/components/admin/NewReservationDialog";
import { EditReservationDialog, type EditableReservation } from "@/components/admin/EditReservationDialog";
import { FreeSlotsSection } from "@/components/admin/FreeSlotsSection";
import { ReservationSourceBadge } from "@/components/admin/ReservationSourceBadge";
import { useState, useEffect, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

/** Minutes since midnight in Europe/Dublin for an ISO timestamp. */
function dublinMinutes(iso: string): number {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Dublin",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).formatToParts(new Date(iso));
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  return h * 60 + m;
}

/** YYYY-MM-DD for an ISO timestamp in Europe/Dublin. */
function dublinYmd(iso: string | Date): string {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Europe/Dublin",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date(iso));
}

function shiftYmd(ymd: string, days: number): string {
  const d = new Date(`${ymd}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function hhmmToMinutes(v: string): number {
  const [h, m] = v.split(":").map(Number);
  return (h || 0) * 60 + (m || 0);
}

/** Sort tables: dart boards first, then pool tables, then regular tables 01…n. */
function sortTablesByLabel<T extends { label: string }>(tables: T[]): T[] {
  const groupRank = (label: string) => {
    const lower = label.toLowerCase();
    if (lower.includes("dart")) return 0;
    if (lower.includes("pool table")) return 1;
    return 2;
  };
  const extractNum = (label: string) => {
    const match = label.match(/\d+/);
    return match ? Number(match[0]) : Infinity;
  };
  return [...tables].sort((a, b) => {
    const ga = groupRank(a.label);
    const gb = groupRank(b.label);
    if (ga !== gb) return ga - gb;
    return extractNum(a.label) - extractNum(b.label) || a.label.localeCompare(b.label);
  });
}

/** All table ids linked to a reservation (legacy single + join table). */
function tableIdsOf(r: {
  table_id?: string | null;
  reservation_tables?: { table_id: string }[] | null;
}): string[] {
  const ids = new Set<string>();
  if (r.table_id) ids.add(r.table_id);
  for (const l of r.reservation_tables ?? []) if (l?.table_id) ids.add(l.table_id);
  return Array.from(ids);
}


export const Route = createFileRoute("/_authenticated/admin/reservations")({
  component: ReservationsPage,
});

type Status =
  | "confirmed"
  | "pending_approval"
  | "pending_payment"
  | "unavailable"
  | "cancelled"
  | "no_show"
  | "checked_in"
  | "seated"
  | "completed";

function ReservationsPage() {
  const list = useServerFn(adminListReservations);
  const update = useServerFn(adminUpdateReservationStatus);
  const removeReservation = useServerFn(adminDeleteReservation);
  const listTablesFn = useServerFn(adminListTables);
  const roleFn = useServerFn(getMyRole);
  const qc = useQueryClient();
  const [filter, setFilter] = useState<string>("all");
  const [includePast, setIncludePast] = useState(false);

  const { data: roleData } = useQuery({ queryKey: ["my-role"], queryFn: () => roleFn() });
  const roles = roleData?.roles ?? [];
  // Floor staff (iPad view): read-only + check-in/seat/close-tab. Nothing else.
  const isFloorOnly = roles.length > 0 && roles.every((r) => r === "floor");
  const canEdit = !isFloorOnly;
  // Permanent deletion is limited to owners and managers.
  const canDelete = roles.some((r) => r === "owner" || r === "manager");

  const { data } = useQuery({
    queryKey: ["admin-reservations", includePast],
    queryFn: () => list({ data: { includePast } }),
  });
  const { data: tables = [] } = useQuery({
    queryKey: ["admin-tables"],
    queryFn: () => listTablesFn(),
  });

  // Live sync: refetch whenever reservations or tables change
  const [isLive, setIsLive] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  useEffect(() => {
    const ch = supabase
      .channel("admin-reservations-sync")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "reservations" },
        () => {
          qc.invalidateQueries({ queryKey: ["admin-reservations"] });
          qc.invalidateQueries({ queryKey: ["admin-summary"] });
          setLastUpdate(new Date());
        },
      )
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "tables" },
        () => {
          qc.invalidateQueries({ queryKey: ["admin-tables"] });
          setLastUpdate(new Date());
        },
      )
      .subscribe((status) => {
        setIsLive(status === "SUBSCRIBED");
      });
    return () => {
      setIsLive(false);
      supabase.removeChannel(ch);
    };
  }, [qc]);

  // Tick every 30s so "updated Xs ago" stays fresh without extra events
  const [, forceTick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => forceTick((n) => n + 1), 30_000);
    return () => clearInterval(id);
  }, []);

  async function setStatus(id: string, status: Status, table_id?: string | null) {
    await update({ data: { id, status, ...(table_id !== undefined ? { table_id } : {}) } });
    qc.invalidateQueries({ queryKey: ["admin-reservations"] });
    qc.invalidateQueries({ queryKey: ["admin-tables"] });
    qc.invalidateQueries({ queryKey: ["admin-summary"] });
    qc.invalidateQueries({ queryKey: ["tables"] });
    qc.invalidateQueries({ queryKey: ["table-reservations"] });
  }

  const [deletingId, setDeletingId] = useState<string | null>(null);

  async function deleteReservation(id: string, label: string) {
    if (!window.confirm(`Delete the booking for ${label}? This cannot be undone.`)) return;
    setDeletingId(id);
    try {
      await removeReservation({ data: { id } });
      qc.invalidateQueries({ queryKey: ["admin-reservations"] });
      qc.invalidateQueries({ queryKey: ["admin-tables"] });
      qc.invalidateQueries({ queryKey: ["admin-summary"] });
      qc.invalidateQueries({ queryKey: ["admin-calendar"] });
      qc.invalidateQueries({ queryKey: ["tables"] });
      qc.invalidateQueries({ queryKey: ["table-reservations"] });
    } catch (err) {
      window.alert(err instanceof Error ? err.message : "Could not delete this booking.");
    } finally {
      setDeletingId(null);
    }
  }



  async function assignTables(id: string, table_ids: string[]) {
    const unique = Array.from(new Set(table_ids));
    await update({ data: { id, status: "confirmed", table_ids: unique } });
    qc.invalidateQueries({ queryKey: ["admin-reservations"] });
    qc.invalidateQueries({ queryKey: ["admin-tables"] });
    qc.invalidateQueries({ queryKey: ["tables"] });
    qc.invalidateQueries({ queryKey: ["table-reservations"] });
  }


  const [editing, setEditing] = useState<EditableReservation | null>(null);

  // Time-range filter (Dublin local time, HH:MM). Defaults cover the full day.
  const [fromTime, setFromTime] = useState("00:00");
  const [toTime, setToTime] = useState("23:59");
  // Day filter (Dublin). Empty string = all days.
  const [day, setDay] = useState<string>(() => dublinYmd(new Date()));
  const [sourceFilter, setSourceFilter] = useState<string>("all");

  const rows = useMemo(() => {
    const fromM = hhmmToMinutes(fromTime);
    const toM = hhmmToMinutes(toTime);
    const seen = new Set<string>();
    const today = dublinYmd(new Date());
    const weekEnd = shiftYmd(today, 6);
    return (data ?? []).filter((r) => {
      // Safety net: never draw the same booking twice, whatever the data says.
      if (seen.has(r.id)) return false;
      const statusMatch = filter === "all" || r.status === filter;
      if (!statusMatch) return false;
      if (sourceFilter !== "all" && ((r as { source?: string }).source ?? "website") !== sourceFilter) return false;
      const ymd = dublinYmd(r.starts_at);
      if (day === "week") {
        if (ymd < today || ymd > weekEnd) return false;
      } else if (day && ymd !== day) return false;
      const startM = dublinMinutes(r.starts_at);
      const endM = startM + (r.duration_minutes ?? 0);
      // Overlap between [startM, endM] and [fromM, toM]
      if (!(startM <= toM && endM >= fromM)) return false;
      seen.add(r.id);
      return true;
    });
  }, [data, filter, fromTime, toTime, day, sourceFilter]);

  /** How many bookings each phone number has in the loaded set (all dates). */
  const perGuest = useMemo(() => {
    const map = new Map<string, { id: string; starts_at: string }[]>();
    for (const r of data ?? []) {
      const g = Array.isArray(r.guest) ? r.guest[0] : r.guest;
      const key = g?.phone?.trim();
      if (!key) continue;
      if (["cancelled", "no_show"].includes(r.status)) continue;
      const list = map.get(key) ?? [];
      list.push({ id: r.id, starts_at: r.starts_at });
      map.set(key, list);
    }
    for (const list of map.values()) list.sort((a, b) => a.starts_at.localeCompare(b.starts_at));
    return map;
  }, [data]);



  return (
    <div>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-3xl">Reservations</h1>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium ${
              isLive
                ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                : "border-amber-500/40 bg-amber-500/10 text-amber-400"
            }`}
            title={
              lastUpdate
                ? `Last change ${Math.max(1, Math.round((Date.now() - lastUpdate.getTime()) / 1000))}s ago`
                : "Waiting for the first change…"
            }
          >
            <span
              className={`h-2 w-2 rounded-full ${
                isLive ? "animate-pulse bg-emerald-400" : "bg-amber-400"
              }`}
            />
            {isLive ? "Live" : "Reconnecting…"}
            {isLive && lastUpdate && (
              <span className="text-muted-foreground">
                · {Math.max(1, Math.round((Date.now() - lastUpdate.getTime()) / 1000))}s ago
              </span>
            )}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {canEdit && (
            <NewReservationDialog
              tables={tables}
              onEditExisting={(b) =>
                setEditing({
                  id: b.id,
                  reference: b.reference,
                  booking_type: b.booking_type,
                  starts_at: b.starts_at,
                  duration_minutes: b.duration_minutes ?? 90,
                  party_size: b.party_size,
                  notes: b.notes ?? null,
                })
              }
            />
          )}
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="rounded-md border border-border bg-background px-3 py-2 text-sm"
          >
            <option value="all">All</option>
            <option value="pending_approval">Pending approval</option>
            <option value="pending_payment">Pending payment</option>
            <option value="confirmed">Confirmed</option>
            <option value="checked_in">Checked in</option>
            <option value="seated">Seated</option>
            <option value="no_show">No-show</option>
            <option value="cancelled">Cancelled</option>
          </select>
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-3 rounded-xl border border-border bg-card/30 p-3">
        <div className="flex min-w-0 max-w-full flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Day</label>
          <div className="flex flex-wrap items-center gap-1">
            <button
              type="button"
              aria-label="Previous day"
              onClick={() => setDay((d) => shiftYmd(d && d !== "week" ? d : dublinYmd(new Date()), -1))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm hover:bg-muted/40"
            >
              &lsaquo;
            </button>
            <input
              type="date"
              value={day === "week" ? "" : day}
              onChange={(e) => setDay(e.target.value)}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm"
            />
            <button
              type="button"
              aria-label="Next day"
              onClick={() => setDay((d) => shiftYmd(d && d !== "week" ? d : dublinYmd(new Date()), 1))}
              className="rounded-md border border-border bg-background px-2 py-1 text-sm hover:bg-muted/40"
            >
              &rsaquo;
            </button>
            <button
              type="button"
              onClick={() => setDay(dublinYmd(new Date()))}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40"
            >
              Today
            </button>
            <button
              type="button"
              onClick={() => setDay(shiftYmd(dublinYmd(new Date()), 1))}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40"
            >
              Tomorrow
            </button>
            <button
              type="button"
              onClick={() => setDay("week")}
              className={`rounded-md border px-2 py-1 text-xs hover:bg-muted/40 ${day === "week" ? "border-primary text-primary" : "border-border bg-background"}`}
            >
              Next 7 days
            </button>
            <button
              type="button"
              onClick={() => setDay("")}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40"
            >
              All days
            </button>
          </div>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">Booked via</label>
          <select
            value={sourceFilter}
            onChange={(e) => setSourceFilter(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          >
            <option value="all">All channels</option>
            <option value="website">Website</option>
            <option value="whatsapp">WhatsApp</option>
            <option value="instagram">Instagram</option>
            <option value="phone">Phone</option>
            <option value="admin">Staff</option>
          </select>
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">From</label>
          <input
            type="time"
            value={fromTime}
            onChange={(e) => setFromTime(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-col">
          <label className="text-[10px] uppercase tracking-wider text-muted-foreground">To</label>
          <input
            type="time"
            value={toTime}
            onChange={(e) => setToTime(e.target.value)}
            className="rounded-md border border-border bg-background px-2 py-1 text-sm"
          />
        </div>
        <div className="flex flex-wrap gap-1.5">
          {[
            { label: "All day", from: "00:00", to: "23:59" },
            { label: "Lunch", from: "12:00", to: "15:00" },
            { label: "Afternoon", from: "15:00", to: "18:00" },
            { label: "Evening", from: "18:00", to: "22:00" },
            { label: "Late", from: "22:00", to: "23:59" },
          ].map((p) => (
            <button
              key={p.label}
              type="button"
              onClick={() => {
                setFromTime(p.from);
                setToTime(p.to);
              }}
              className="rounded-md border border-border bg-background px-2 py-1 text-xs hover:bg-muted/40"
            >
              {p.label}
            </button>
          ))}
        </div>
        <label className="ml-auto inline-flex cursor-pointer items-center gap-2 text-xs text-muted-foreground">
          <input
            type="checkbox"
            checked={includePast}
            onChange={(e) => setIncludePast(e.target.checked)}
            className="rounded border-border bg-background text-primary"
          />
          Include past reservations
        </label>
      </div>
      <div className="mt-1 text-right text-xs text-muted-foreground">
        Showing {rows.length} of {(data ?? []).length} reservations ({day || "all days"} · {fromTime}–{toTime})
      </div>

      {/* Quick list: date · time · name · phone with one-tap confirm / cancel */}
      <div className="mt-6 rounded-xl border border-border">
        <div className="flex items-center justify-between border-b border-border bg-muted/30 px-4 py-2">
          <h2 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
            Quick list · confirm or cancel
          </h2>
          <span className="text-xs text-muted-foreground">{rows.length} bookings</span>
        </div>
        <ul className="divide-y divide-border">
          {rows.map((r) => {
            const guest = Array.isArray(r.guest) ? r.guest[0] : r.guest;
            const when = new Date(r.starts_at);
            const date = when.toLocaleDateString("en-IE", {
              timeZone: "Europe/Dublin",
              weekday: "short",
              day: "2-digit",
              month: "short",
            });
            const time = when.toLocaleTimeString("en-IE", {
              timeZone: "Europe/Dublin",
              hour: "2-digit",
              minute: "2-digit",
              hour12: false,
            });
            const tableLabels = tableIdsOf(r)
              .map((id) => tables.find((t) => t.id === id)?.label)
              .filter(Boolean)
              .join(" + ");
            const isCancelled = r.status === "cancelled" || r.status === "no_show" || r.status === "unavailable";
            return (
              <li
                key={`quick-${r.id}`}
                className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 text-sm"
              >
                <div className="w-28 shrink-0">
                  <div className="font-medium">{date}</div>
                  <div className="text-xs text-muted-foreground">{time}</div>
                </div>
                <div className="min-w-[9rem] flex-1">
                  <div className="font-medium">{guest?.full_name ?? "—"}</div>
                  {guest?.phone && (
                    <a href={`tel:${guest.phone}`} className="text-xs text-muted-foreground underline-offset-2 hover:underline">
                      {guest.phone}
                    </a>
                  )}
                </div>
                <div className="text-xs text-muted-foreground">
                  {r.party_size} pax · {r.booking_type.replaceAll("_", " ")}
                  {tableLabels ? ` · ${tableLabels}` : ""}
                </div>
                <ReservationSourceBadge source={(r as { source?: string }).source} />
                <span
                  className={`rounded-full border px-2 py-0.5 text-[11px] ${
                    r.status === "confirmed"
                      ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
                      : isCancelled
                        ? "border-red-500/40 bg-red-500/10 text-red-400"
                        : "border-amber-500/40 bg-amber-500/10 text-amber-400"
                  }`}
                >
                  {r.status.replaceAll("_", " ")}
                </span>
                {canEdit && (
                  <div className="ml-auto flex gap-1">
                    <Button
                      size="sm"
                      disabled={r.status === "confirmed"}
                      onClick={() => setStatus(r.id, "confirmed")}
                    >
                      Confirm
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={r.status === "cancelled"}
                      onClick={() => setStatus(r.id, "cancelled")}
                    >
                      Cancel
                    </Button>
                    {canDelete && (
                      <Button
                        size="sm"
                        variant="destructive"
                        disabled={deletingId === r.id}
                        onClick={() => deleteReservation(r.id, guest?.full_name ?? r.reference)}
                      >
                        {deletingId === r.id ? "Deleting…" : "Delete"}
                      </Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
          {rows.length === 0 && (
            <li className="px-4 py-6 text-center text-sm text-muted-foreground">No reservations.</li>
          )}
        </ul>
      </div>

      <div className="mt-6 overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/30 text-xs uppercase tracking-wider text-muted-foreground">
            <tr>
              <th className="p-3 text-left">Ref</th>
              <th className="p-3 text-left">When</th>
              <th className="p-3 text-left">Type</th>
              <th className="p-3 text-left">Party</th>
              <th className="p-3 text-left">Guest</th>
              <th className="p-3 text-left">Table</th>
              <th className="p-3 text-left">Status</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const guest = Array.isArray(r.guest) ? r.guest[0] : r.guest;
              const siblings = guest?.phone ? (perGuest.get(guest.phone.trim()) ?? []) : [];
              const position = siblings.findIndex((s) => s.id === r.id) + 1;
              const eligibleTables = sortTablesByLabel(
                tables.filter((t) =>
                  r.booking_type === "pool_table"
                    ? t.resource_type === "pool_table"
                    : t.resource_type !== "pool_table",
                ),
              );
              return (
                <tr key={r.id} className="border-t border-border">
                  <td className="p-3 font-mono text-xs">
                    {r.reference}
                    <div className="mt-1">
                      <ReservationSourceBadge
                        source={(r as { source?: string }).source}
                        className="font-sans"
                      />
                    </div>
                    {(r as { created_at?: string }).created_at && (
                      <div className="mt-0.5 font-sans text-[10px] text-muted-foreground">
                        taken{" "}
                        {new Date((r as { created_at: string }).created_at).toLocaleDateString("en-IE", {
                          timeZone: "Europe/Dublin",
                          day: "2-digit",
                          month: "short",
                        })}
                      </div>
                    )}
                  </td>
                  <td className="p-3">
                    {new Date(r.starts_at).toLocaleString("en-IE", { timeZone: "Europe/Dublin" })}
                  </td>
                  <td className="p-3">{r.booking_type.replaceAll("_", " ")}</td>
                  <td className="p-3">{r.party_size}</td>
                  <td className="p-3">
                    {guest?.full_name}
                    <div className="text-xs text-muted-foreground">{guest?.phone}</div>
                    {siblings.length > 1 && position > 0 && (
                      <span className="mt-1 inline-block rounded-full border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[10px] text-amber-400">
                        booking {position} of {siblings.length} for this guest
                      </span>
                    )}
                  </td>
                  <td className="p-3">
                    {(() => {
                      const assigned = tableIdsOf(r);
                      const labels = assigned
                        .map((id) => tables.find((t) => t.id === id)?.label)
                        .filter(Boolean) as string[];
                      if (!canEdit) {
                        return (
                          <span className="text-xs text-muted-foreground">
                            {labels.join(" + ") || "—"}
                          </span>
                        );
                      }
                      return (
                        <div className="min-w-[10rem] space-y-1">
                          <select
                            multiple
                            size={Math.min(5, eligibleTables.length || 1)}
                            value={assigned}
                            onChange={(e) => {
                              const selected = Array.from(e.target.selectedOptions).map((o) => o.value);
                              assignTables(r.id, selected);
                            }}
                            className="w-full rounded-md border border-border bg-background px-2 py-1 text-xs"
                          >
                            {eligibleTables.map((t) => (
                              <option key={t.id} value={t.id}>
                                {t.label} ({t.capacity})
                              </option>
                            ))}
                            {eligibleTables.length === 0 && (
                              <option disabled>No tables</option>
                            )}
                          </select>
                          <p className="text-[11px] text-muted-foreground">
                            {labels.length ? labels.join(" + ") : "No table yet"}
                          </p>
                        </div>
                      );
                    })()}
                  </td>

                  <td className="p-3">
                    <span className="rounded-full border border-border bg-muted/30 px-2 py-0.5 text-xs">
                      {r.status}
                    </span>
                  </td>
                  <td className="p-3">
                    <div className="flex flex-wrap justify-end gap-1">
                      {canEdit && r.status === "pending_approval" && (
                        <>
                          <Button size="sm" onClick={() => setStatus(r.id, "confirmed")}>
                            Approve
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "unavailable")}>
                            Decline
                          </Button>
                        </>
                      )}
                      {r.status === "confirmed" && (
                        <>
                          <Button size="sm" onClick={() => setStatus(r.id, "checked_in")}>
                            Check in / Occupy
                          </Button>
                          {canEdit && (
                            <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "no_show")}>
                              No-show
                            </Button>
                          )}
                        </>
                      )}
                      {r.status === "checked_in" && (
                        <Button size="sm" variant="outline" onClick={() => setStatus(r.id, "seated")}>
                          Seat
                        </Button>
                      )}
                      {(r.status === "checked_in" || r.status === "seated") && (
                        <Button size="sm" onClick={() => setStatus(r.id, "completed")}>
                          Close tab / Free table
                        </Button>
                      )}
                      {canEdit && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() =>
                            setEditing({
                              id: r.id,
                              reference: r.reference,
                              booking_type: r.booking_type,
                              starts_at: r.starts_at,
                              duration_minutes: r.duration_minutes,
                              party_size: r.party_size,
                              notes: r.notes,
                              preferred_area: r.preferred_area,
                              guest_full_name: guest?.full_name ?? null,
                              guest_phone: guest?.phone ?? null,
                              guest_email: guest?.email ?? null,
                            })
                          }
                        >
                          Edit
                        </Button>
                      )}
                      {canEdit && (
                        <Button size="sm" variant="ghost" onClick={() => setStatus(r.id, "cancelled")}>
                          Cancel
                        </Button>
                      )}
                      {canDelete && (
                        <Button
                          size="sm"
                          variant="destructive"
                          disabled={deletingId === r.id}
                          onClick={() => deleteReservation(r.id, guest?.full_name ?? r.reference)}
                        >
                          {deletingId === r.id ? "Deleting…" : "Delete"}
                        </Button>
                      )}
                      {isFloorOnly && r.status !== "confirmed" && r.status !== "checked_in" && r.status !== "seated" && (
                        <span className="text-xs text-muted-foreground">View only</span>
                      )}
                    </div>
                  </td>

                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={8} className="p-8 text-center text-sm text-muted-foreground">
                  No reservations.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-6">
        <FreeSlotsSection />
      </div>

      <EditReservationDialog reservation={editing} onClose={() => setEditing(null)} />
    </div>
  );
}
